import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceCardRow, InvoiceGridRow, InvoiceRow } from './InvoiceRows';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';

/** InvoicesGrid — #62 next-phase Invoices depth spec (UXeng §2): a TABLE (Invoice #/Account/Amount/Due
 *  Date/Status), same responsive tier-switch as Pipeline/Accounts. Status has no inline edit here —
 *  it's action-driven from the detail panel (Send/Mark paid/Void), never a free picker from the row. */
export const InvoicesGrid: React.FC<{
  selectedId?: string | null;
  onOpenInvoice: (invoice: InvoiceRow) => void;
  refreshKey?: number;
}> = ({ selectedId, onOpenInvoice, refreshKey }) => {
  const { t } = useTranslation('common');

  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // X-1 via useListFetch (P38 surface 8 of 10) — a failed invoice list must never read as
  // "nothing is owed to you".
  const list = useListFetch<InvoiceRow[]>('/api/invoices', { refreshKey });
  const invoices = list.data ?? [];

  return (
    <div className="relative w-full">
      <ListStateShell status={list.status} testIdBase="invoices-grid" onRetry={list.retry}>
      {!isDesktop ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
          {invoices.map((i) => <InvoiceCardRow key={i.id} invoice={i} selected={i.id === selectedId} onOpenInvoice={onOpenInvoice} />)}
          {invoices.length === 0 && <div className="col-span-full text-center text-sm text-text-muted py-10">{t('invoicesGrid.empty')}</div>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-start border-collapse">
            <thead>
              <tr className="text-label-caps uppercase text-text-muted border-b border-border-neutral">
                <th className="sticky z-10 bg-surface p-cell-y px-cell-x text-start [inset-inline-start:0]">{t('invoicesGrid.columns.account')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('invoicesGrid.columns.amount')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('invoicesGrid.columns.dueDate')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('invoicesGrid.columns.status')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('invoicesGrid.columns.number')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => <InvoiceGridRow key={i.id} invoice={i} selected={i.id === selectedId} onOpenInvoice={onOpenInvoice} />)}
            </tbody>
          </table>
          {invoices.length === 0 && <div className="text-center text-sm text-text-muted py-10">{t('invoicesGrid.empty')}</div>}
        </div>
      )}
      </ListStateShell>
    </div>
  );
};
